/*
 * Cart drawer upsells — tap-to-expand product image gallery.
 *
 * Registered as a custom element on purpose: CartDrawer.renderContents() swaps
 * the whole .drawer__inner via innerHTML on every cart mutation, and scripts
 * inserted that way never execute. Custom elements upgrade automatically, so
 * this keeps working after an add / remove / quantity change.
 *
 * The overlay is appended to <body> rather than inside the drawer so it is not
 * destroyed mid-interaction by a re-render, and so it can sit above the
 * drawer's z-index: 1000.
 *
 * Image data arrives as a JSON attribute rather than an inline
 * <script type="application/json">, because the theme's own ProductInfoUpsell
 * grabs the first such script inside the card for its variant data — a second
 * one would shadow it and break variant switching.
 */
(function () {
  if (customElements.get('cart-upsell-media')) return;

  var OVERLAY_ID = 'CartUpsellZoom';

  function closeOverlay() {
    var overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) return;

    var opener = overlay.opener;
    overlay.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);

    window.setTimeout(function () {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 200);

    // Only restore focus if the trigger survived — a cart re-render while the
    // overlay was open would have replaced it.
    if (opener && document.contains(opener)) opener.focus();
  }

  function onKeydown(event) {
    var overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.stopPropagation();
      closeOverlay();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      overlay.goTo(overlay.index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      overlay.goTo(overlay.index - 1);
    }
  }

  function svgIcon(path) {
    return '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="' +
      path + '"/></svg>';
  }

  function openOverlay(images, startIndex, opener) {
    closeOverlay();
    if (!images.length) return;

    var overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'cart-upsell-zoom';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', images[0].alt || 'Product images');
    overlay.opener = opener;
    overlay.index = 0;

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'cart-upsell-zoom__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = svgIcon('M18.3 5.7l-1.4-1.4-4.9 4.9-4.9-4.9-1.4 1.4 4.9 4.9-4.9 4.9 1.4 1.4 4.9-4.9 4.9 4.9 1.4-1.4-4.9-4.9z');

    // Horizontal scroll-snap track: swiping is native, which behaves far better
    // on touch than any transform-driven carousel.
    var track = document.createElement('div');
    track.className = 'cart-upsell-zoom__track';

    images.forEach(function (image, i) {
      var slide = document.createElement('div');
      slide.className = 'cart-upsell-zoom__slide';
      var img = document.createElement('img');
      img.className = 'cart-upsell-zoom__img';
      img.src = image.src;
      img.alt = image.alt || '';
      // Only the opening image is urgent; the rest can stream in.
      img.loading = i === startIndex ? 'eager' : 'lazy';
      slide.appendChild(img);
      track.appendChild(slide);
    });

    overlay.appendChild(close);
    overlay.appendChild(track);

    var dots = null;
    var counter = null;
    var prev = null;
    var next = null;

    if (images.length > 1) {
      prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'cart-upsell-zoom__arrow cart-upsell-zoom__arrow--prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = svgIcon('M15.4 7.4L14 6l-6 6 6 6 1.4-1.4L10.8 12z');

      next = document.createElement('button');
      next.type = 'button';
      next.className = 'cart-upsell-zoom__arrow cart-upsell-zoom__arrow--next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = svgIcon('M8.6 7.4L10 6l6 6-6 6-1.4-1.4 4.6-4.6z');

      dots = document.createElement('div');
      dots.className = 'cart-upsell-zoom__dots';
      images.forEach(function (image, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cart-upsell-zoom__dot';
        dot.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + images.length);
        dot.addEventListener('click', function () {
          overlay.goTo(i);
        });
        dots.appendChild(dot);
      });

      counter = document.createElement('span');
      counter.className = 'cart-upsell-zoom__counter';

      overlay.appendChild(prev);
      overlay.appendChild(next);
      overlay.appendChild(dots);
      overlay.appendChild(counter);
    }

    document.body.appendChild(overlay);

    overlay.goTo = function (i) {
      var clamped = Math.max(0, Math.min(images.length - 1, i));
      track.scrollTo({ left: track.clientWidth * clamped, behavior: 'smooth' });
    };

    function sync() {
      var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (i === overlay.index && dots && dots.querySelector('.is-active')) return;
      overlay.index = i;
      if (dots) {
        Array.prototype.forEach.call(dots.children, function (dot, n) {
          dot.classList.toggle('is-active', n === i);
        });
      }
      if (counter) counter.textContent = (i + 1) + ' / ' + images.length;
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === images.length - 1;
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        sync();
        ticking = false;
      });
    });

    if (prev) prev.addEventListener('click', function () { overlay.goTo(overlay.index - 1); });
    if (next) next.addEventListener('click', function () { overlay.goTo(overlay.index + 1); });

    close.addEventListener('click', closeOverlay);
    overlay.addEventListener('click', function (event) {
      // Backdrop only — tapping the photo itself must not dismiss it.
      if (event.target === overlay || event.target === track ||
          event.target.classList.contains('cart-upsell-zoom__slide')) {
        closeOverlay();
      }
    });
    document.addEventListener('keydown', onKeydown);

    window.requestAnimationFrame(function () {
      overlay.classList.add('is-open');
      // Jump (no animation) to the tapped image, then reflect it in the chrome.
      track.scrollLeft = track.clientWidth * startIndex;
      overlay.index = startIndex;
      sync();
      close.focus();
    });
  }

  customElements.define(
    'cart-upsell-media',
    class CartUpsellMedia extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.trigger = this.querySelector('.cart-upsell__media-btn');
        if (!this.trigger) return;

        this.trigger.addEventListener('click', (event) => {
          event.preventDefault();
          openOverlay(this.images(), 0, this.trigger);
        });
      }

      images() {
        if (this.parsed) return this.parsed;
        var raw = this.dataset.images;
        try {
          this.parsed = raw ? JSON.parse(raw) : [];
        } catch (err) {
          this.parsed = [];
        }
        // Fall back to the single featured image if the list is unusable.
        if (!this.parsed.length && this.dataset.fullSrc) {
          this.parsed = [{ src: this.dataset.fullSrc, alt: this.dataset.alt || '' }];
        }
        return this.parsed;
      }
    }
  );
})();
